'use strict';

import PropTypes from 'prop-types';

import React from 'react';
import { formatUrl } from '../../utils/urlUtil';
import classNames from 'classnames';
import { useRouter } from 'next/router';

function getFriendlyUrl(location) {
    if (typeof location === 'string') {
        return location;
    }

    return formatUrl(location.pathname, location.query) + (location.hash ? '#' + location.hash : '');
}

function ActiveLink({ children, to, onClick, activeClassName, className, state, ...rest }) {
    const router = useRouter();
    const href = getFriendlyUrl(to);

    const handleClick = (e) => {
        onClick(e);

        if (href.length > 0) {
            const { target } = e.currentTarget;

            if (
                (target && target !== '_self') ||
                e.metaKey ||
                e.ctrlKey ||
                e.shiftKey ||
                e.altKey || // triggers resource download
                (e.nativeEvent && e.nativeEvent.which === 2)
            ) {
                return;
            }

            e.preventDefault();
            if (to.state) {
                // next-router doesn't have state, so this is a hack
                // Put the state params as query, but hide them using as
                const realHref = formatUrl(href, to.state);
                router.push(realHref, href);
            } else {
                router.push(href);
            }
        }
    };

    const classes = classNames(className, {
        [activeClassName]: router.pathname === to.pathname,
    });

    return (
        <a href={href} onClick={handleClick} className={classes} {...rest}>
            {children}
        </a>
    );
}

class PlayLink extends React.Component {
    constructor(props) {
        super(props);

        this._onClickHandler = this._onClickHandler.bind(this);
    }

    _onClickHandler(e) {
        if (!this.props.isExternal && !this.props.networkOnline) {
            e.preventDefault();
        }

        if (this.props.onAfterClick) {
            this.props.onAfterClick(e);
        }
    }

    createLocationDescriptor(to, query = {}, hash = '', state = {}) {
        const location = {
            query,
            hash,
            state,
        };

        if (typeof to === 'string') {
            const hashParts = to.split('#');
            const qsParts = hashParts[0].split('?');
            location.pathname = qsParts[0];

            //qs
            if (qsParts.length > 1) {
                //qs
                qsParts[1].split('&').forEach((str) => {
                    const qsPart = str.split('=');
                    location.query[qsPart[0]] = qsPart.length > 1 ? qsPart[1] : '';
                });
            }

            //hash
            if (hashParts.length > 1) {
                location.hash = `#${hashParts[1]}`;
            }

            return Object.freeze(location);
        }

        return Object.assign({}, location, to);
    }

    render() {
        const {
            to,
            query,
            hash,
            activeClassName,
            children,
            isExternal,
            className,
            id,
            onClick,
            tabIndex,
            onMouseDown,
            onFocus,
            state,
            role,
            blurOnClick,
            regTestSpec,
        } = this.props;

        const onMouseUp = blurOnClick
            ? (e) => {
                  e.currentTarget.blur();
              }
            : null;

        // We get error from react if we just use ...other from above spread
        const attr = {
            id: id,
            className: className,
            tabIndex: tabIndex,
            role: role,
            onMouseDown: onMouseDown,
            onMouseUp: onMouseUp,
            onFocus: onFocus,
            'aria-controls': this.props['aria-controls'],
            'aria-selected': this.props['aria-selected'],
            'aria-hidden': this.props['aria-hidden'],
            'aria-label': this.props['aria-label'],
            'data-rt-spec': regTestSpec,
            'data-rt': regTestSpec ? 'play-link' : undefined,
        };
        const location = this.createLocationDescriptor(to, query, hash, state);

        const friendlyUrl = getFriendlyUrl(location);
        const clickHandler = onClick ? onClick : this._onClickHandler;

        //check if external link or if there's no internal router matching that name
        if (isExternal) {
            return (
                <a onClick={clickHandler} href={friendlyUrl} {...attr}>
                    {children}
                </a>
            );
        }

        // let LinkComponent = Link;

        return (
            <ActiveLink to={location} activeClassName={activeClassName} onClick={clickHandler} {...attr}>
                {children}
            </ActiveLink>
        );
    }
}

PlayLink.propTypes = {
    to: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
    query: PropTypes.object,
    hash: PropTypes.string,
    state: PropTypes.object,
    activeClassName: PropTypes.string,
    activeStyle: PropTypes.string,
    children: PropTypes.node,
    isExternal: PropTypes.bool,
    isIndexLink: PropTypes.bool,
    scrollToTop: PropTypes.bool,
    onClick: PropTypes.func,
    onAfterClick: PropTypes.func,
    onFocus: PropTypes.func,
    regTestSpec: PropTypes.string,
};

PlayLink.defaultProps = {
    scrollToTop: true,
};

export default PlayLink;
